import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AnnouncementTagTabValue } from '../const'

import { AnnouncementDetailResource } from '@/api/@types'
import { ErrorOutlinedIcon } from '@/components/icons/ErrorOutlined'
import {
  Button,
  Icon,
  IconWrapper,
  InputCheckbox,
  Pagination,
} from '@/components/ui'
import { ROUTE } from '@/const'
import {
  getAnnouncementDetail,
  useBulkMarkAnnouncementsRead,
  useListAnnouncements,
  useMarkAnnouncementRead,
} from '@/hooks/features/announcement/announcements'
import { announcementKeyFactory } from '@/hooks/features/announcement/key'
import { useModal } from '@/hooks/util'

import { AnnouncementCard } from '../AnnouncementCard'
import { AnnouncementDetailModal } from '../AnnouncementDetailModal'
import { ANNOUNCEMENT_TAG_TABS } from '../const'
import { MAX_PAGES, PAGE_SIZE } from './const'

const capLastPage = (last?: number) =>
  Math.min(Math.max(last ?? 1, 1), MAX_PAGES)

export const AnnouncementCardList = () => {
  const [unreadPage, setUnreadPage] = useState(1)
  const [readPage, setReadPage] = useState(1)
  const [unreadLastPage, setUnreadLastPage] = useState(1)
  const [readLastPage, setReadLastPage] = useState(1)
  const [showRead, setShowRead] = useState<boolean>(false)
  const [selectedTag, setSelectedTag] =
    useState<AnnouncementTagTabValue>('important')
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const { data, isFetching } = useListAnnouncements({
    query: {
      page: showRead ? readPage : unreadPage,
      size: PAGE_SIZE,
      ...(selectedTag !== 'all' ? { tag: selectedTag } : {}),
    },
    readStatus: showRead ? 'read' : 'unread',
  })

  const { t } = useTranslation()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { isOpen, open, close } = useModal()
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<AnnouncementDetailResource | null>(null)
  const [fetchingId, setFetchingId] = useState<number | null>(null)
  const shouldScrollToTopRef = useRef(false)
  const listRef = useRef<HTMLDivElement>(null)

  const { mutate: markAnnouncementRead } = useMarkAnnouncementRead()
  const { mutate: bulkMarkAnnouncementsRead, isLoading: isBulkMarkLoading } =
    useBulkMarkAnnouncementsRead()

  const announcements = data?.announcements ?? []
  const visibleIds = announcements.map((announcement) => announcement.id)
  const selectedVisibleIds = selectedIds.filter((id) => visibleIds.includes(id))
  const isAllSelected =
    announcements.length > 0 &&
    selectedVisibleIds.length === announcements.length
  const isIndeterminate = selectedVisibleIds.length > 0 && !isAllSelected
  const isBusy = fetchingId !== null || isBulkMarkLoading

  const handleOpen = async (id: number) => {
    if (fetchingId !== null) return

    setFetchingId(id)
    try {
      const data = await queryClient.fetchQuery({
        queryKey: announcementKeyFactory.detail({
          pathParams: { announcementId: id },
        }),
        queryFn: () =>
          getAnnouncementDetail({ pathParams: { announcementId: id } }),
      })

      markAnnouncementRead(
        { announcementId: id },
        {
          onSuccess: () => {
            invalidateAnnouncementLists()
          },
        }
      )

      if (data.announcementType === 'general') {
        setSelectedAnnouncement(data)
        open()
      }

      const urlDate = data?.priceChange?.effectiveDate ?? ''

      if (data.announcementType === 'priceChange') {
        router.push({
          pathname: ROUTE.productHistory,
          query: { changeType: 'price', dateFrom: urlDate, dateTo: urlDate },
        })
      }
      if (data.announcementType === 'productCodeChange') {
        router.push({
          pathname: ROUTE.productHistory,
          query: {
            changeType: 'productCode',
            dateFrom: urlDate,
            dateTo: urlDate,
          },
        })
      }
    } finally {
      setFetchingId(null)
    }
  }

  const handleClose = () => {
    setSelectedAnnouncement(null)
    close()
  }

  const handleShowUnread = () => {
    setSelectedIds([])
    setShowRead(false)
  }

  const handleShowRead = () => {
    setSelectedIds([])
    setShowRead(true)
  }

  const handleTagChange = (tag: AnnouncementTagTabValue) => {
    if (selectedTag === tag) return
    setSelectedIds([])
    setSelectedTag(tag)
    setUnreadPage(1)
    setReadPage(1)
  }

  const handlePage = (page: number) => {
    shouldScrollToTopRef.current = true
    setSelectedIds([])
    if (showRead) {
      setReadPage(page)
      return
    }
    setUnreadPage(page)
  }

  const handleToggleSelect = (id: number, checked: boolean) => {
    setSelectedIds(
      checked
        ? [...selectedVisibleIds, id]
        : selectedVisibleIds.filter((selectedId) => selectedId !== id)
    )
  }

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? visibleIds : [])
  }

  const handleBulkMarkRead = () => {
    if (selectedVisibleIds.length === 0 || isBulkMarkLoading) return

    bulkMarkAnnouncementsRead(
      { announcementIds: selectedVisibleIds },
      {
        onSuccess: () => {
          setSelectedIds([])
          invalidateAnnouncementLists()
        },
        onError: () => {
          invalidateAnnouncementLists()
        },
      }
    )
  }

  const invalidateAnnouncementLists = () => {
    queryClient.invalidateQueries({
      queryKey: announcementKeyFactory.announcements(),
    })
  }

  const lastPage = showRead ? readLastPage : unreadLastPage
  const currentPage = showRead ? readPage : unreadPage
  const safePage = Math.min(currentPage, lastPage)

  useEffect(() => {
    if (isFetching || !data) return

    const capped = capLastPage(data.page.last)

    if (showRead) {
      setReadLastPage(capped)
      setReadPage((prev) => Math.min(prev, capped))
    } else {
      setUnreadLastPage(capped)
      setUnreadPage((prev) => Math.min(prev, capped))
    }
  }, [data, isFetching, showRead])

  useEffect(() => {
    if (!shouldScrollToTopRef.current || isFetching) return

    shouldScrollToTopRef.current = false
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [isFetching, safePage])

  return (
    <div
      ref={listRef}
      className={clsx(
        'tw-rounded tw-border tw-border-primary-100',
        'tw-overflow-hidden',
        'tw-scroll-mt-20'
      )}
    >
      <div
        className={clsx(
          'tw-flex tw-h-11 tw-items-center tw-gap-1',
          'tw-px-2',
          'tw-rounded-t tw-border-b tw-border-primary-100',
          'tw-bg-primary-50'
        )}
      >
        <IconWrapper>
          <Icon icon={ErrorOutlinedIcon} size={'2xl'} color={'primary'} />
        </IconWrapper>
        <p className={clsx('tw-text-sm tw-font-bold tw-text-primary-500')}>
          {t('announcement.title')}
        </p>
        <div className="tw-flex tw-ml-auto tw-gap-1 tw-w-32 tw-flex-shrink-0">
          <Button
            variant={showRead ? 'secondary' : 'primary'}
            size={'sm'}
            onClick={handleShowUnread}
          >
            未読
          </Button>
          <Button
            variant={showRead ? 'primary' : 'secondary'}
            size={'sm'}
            onClick={handleShowRead}
          >
            既読
          </Button>
        </div>
      </div>
      <div
        className={clsx(
          'tw-flex tw-flex-wrap tw-gap-1',
          'tw-border-b tw-border-primary-100',
          'tw-px-2 tw-py-2'
        )}
      >
        {ANNOUNCEMENT_TAG_TABS.map(({ value, labelKey }) => {
          const isActive = selectedTag === value
          return (
            <Button
              key={value}
              variant={isActive ? 'primary' : 'secondary'}
              size={'sm'}
              isBlock={false}
              roundType={'full'}
              onClick={() => handleTagChange(value)}
            >
              {t(labelKey)}
            </Button>
          )
        })}
      </div>
      {!showRead && (
        <div
          className={clsx(
            'tw-flex tw-items-center tw-gap-2',
            'tw-border-b tw-border-primary-100',
            'tw-px-2 tw-py-2'
          )}
        >
          <InputCheckbox
            id={'announcement-select-all'}
            checked={isAllSelected}
            isIndeterminate={isIndeterminate}
            label={t('announcement.selectAll')}
            showLabel
            disabled={isBusy || announcements.length === 0}
            handleChange={handleSelectAll}
          />
          <div className="tw-ml-auto tw-w-28 tw-flex-shrink-0">
            <Button
              variant={'primary'}
              size={'sm'}
              isBlock={false}
              disabled={selectedVisibleIds.length === 0}
              isLoading={isBulkMarkLoading}
              onClick={handleBulkMarkRead}
            >
              {t('announcement.markAllRead')}
            </Button>
          </div>
        </div>
      )}
      {!isFetching && !!data && data.announcements.length > 0 ? (
        <ul className={clsx('tw-divide-y tw-divide-gray-300 tw-px-2')}>
          {data.announcements.map((announcement) => {
            const isDisabled = isBusy

            return (
              <li
                key={`${announcement.id}`}
                className={clsx(
                  'tw-py-4',
                  'tw-transition tw-duration-stateChange',
                  isDisabled
                    ? 'tw-cursor-not-allowed tw-opacity-40 tw-blur-[1px] tw-pointer-events-none'
                    : 'tw-cursor-pointer hover:tw-opacity-hover'
                )}
                onClick={() => !isDisabled && handleOpen(announcement.id)}
              >
                <div className={clsx('tw-flex tw-items-start tw-gap-3')}>
                  {!showRead && (
                    <div
                      className={clsx('tw-pt-0.5')}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <InputCheckbox
                        id={`announcement-select-${announcement.id}`}
                        checked={selectedVisibleIds.includes(announcement.id)}
                        label={''}
                        disabled={isBusy}
                        handleChange={(checked) =>
                          handleToggleSelect(announcement.id, checked)
                        }
                      />
                    </div>
                  )}
                  <div className={clsx('tw-min-w-0 tw-flex-1')}>
                    <AnnouncementCard
                      title={announcement.title}
                      displayDate={announcement.displayDate}
                      tag={announcement.tag}
                    />
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      ) : isFetching ? (
        <div className={clsx('tw-p-2 tw-flex tw-flex-col tw-gap-1')}>
          <div
            className={clsx(
              'tw-bg-gray-600/20 tw-animate-pulse tw-h-4 tw-w-32 tw-rounded'
            )}
          ></div>
          <div
            className={clsx(
              'tw-bg-gray-600/40 tw-animate-pulse tw-h-4 tw-w-64 tw-rounded'
            )}
          ></div>
          <div
            className={clsx(
              'tw-bg-gray-600/20 tw-animate-pulse tw-h-4 tw-w-32 tw-rounded'
            )}
          ></div>
        </div>
      ) : (
        <div className={'tw-py-3 tw-px-2 tw-text-gray-700 tw-text-sm'}>
          {showRead
            ? t('announcement.noReadData')
            : t('announcement.noUnReadData')}
        </div>
      )}
      {lastPage > 1 && (
        <div
          className={clsx('tw-py-3 tw-flex tw-items-center tw-justify-center')}
        >
          <Pagination
            current={safePage}
            total={lastPage}
            onClick={handlePage}
          />
        </div>
      )}
      {selectedAnnouncement !== null && (
        <AnnouncementDetailModal
          isOpen={isOpen}
          onClose={handleClose}
          data={selectedAnnouncement}
        />
      )}
    </div>
  )
}
